import type { LoBillingPlanId } from '@/lib/lyrraone-hub';
import type { InstitutionTabId } from '@/lib/institution-tab-registry';
import type { LyrraOneExtraSettingsTabId } from '@/lib/lyrraone-settings-tabs';
import { summerAiPromoGrantsFeature } from '@/lib/lyrraone-summer-ai-promo';

/** Aligné sur `lyrraone-plan-features.service.ts` (backend). */
export type LyrraOnePlanFeature =
  | 'designerAiBlocks'
  | 'brandedCertificates'
  | 'dripContent'
  | 'googleAnalytics'
  | 'detailedAnalytics'
  | 'dataExport'
  | 'coupons'
  | 'stripeSellerCheckout'
  | 'affiliateProgram'
  | 'communities'
  | 'webinars'
  | 'booking'
  | 'smsMarketing'
  | 'physicalProducts'
  | 'expertAiAssistant'
  | 'customDomain'
  | 'blog'
  | 'prioritySupport'
  | 'personalizedOnboarding'
  | 'advancedAccessibility'
  | 'simpleFormationAi';

export type LyrraOnePlanTier = LoBillingPlanId;

const TIER_RANK: Record<LyrraOnePlanTier, number> = {
  discovery: 0,
  creator: 1,
  expert: 2,
};

export const LYRRAONE_FEATURE_MIN_TIER: Record<LyrraOnePlanFeature, LyrraOnePlanTier> = {
  designerAiBlocks: 'creator',
  brandedCertificates: 'creator',
  dripContent: 'creator',
  googleAnalytics: 'creator',
  detailedAnalytics: 'creator',
  dataExport: 'creator',
  coupons: 'creator',
  stripeSellerCheckout: 'creator',
  affiliateProgram: 'creator',
  communities: 'creator',
  webinars: 'creator',
  booking: 'creator',
  smsMarketing: 'creator',
  customDomain: 'expert',
  blog: 'discovery',
  prioritySupport: 'creator',
  advancedAccessibility: 'discovery',
  physicalProducts: 'expert',
  expertAiAssistant: 'expert',
  simpleFormationAi: 'creator',
  personalizedOnboarding: 'expert',
};

export const LYRRAONE_FEATURE_LABELS: Record<LyrraOnePlanFeature, string> = {
  designerAiBlocks: "L'éditeur de blocs IA",
  brandedCertificates: 'Les certificats personnalisés (marque)',
  dripContent: 'Les parcours séquencés (drip)',
  googleAnalytics: 'Google Analytics sur le site',
  detailedAnalytics: 'Les analytics détaillés',
  dataExport: "L'export des données (contacts, ventes)",
  coupons: 'Les coupons & promotions',
  stripeSellerCheckout: "L'encaissement via votre compte Stripe",
  affiliateProgram: "Le programme d'affiliation",
  communities: 'Les communautés privées',
  webinars: 'Les webinaires evergreen',
  booking: 'La prise de rendez-vous en ligne',
  smsMarketing: 'Les envois SMS marketing',
  customDomain: 'Le domaine personnalisé et la marque blanche',
  blog: 'Le blog intégré',
  prioritySupport: 'Le support prioritaire',
  advancedAccessibility: "Les outils d'accessibilité avancés",
  physicalProducts: 'Les produits physiques',
  expertAiAssistant: "L'assistant IA formateur",
  simpleFormationAi: "L'assistant IA formation simple",
  personalizedOnboarding: "L'onboarding personnalisé",
};

export const LYRRAONE_PLAN_LABELS: Record<LyrraOnePlanTier, string> = {
  discovery: 'Gratuit',
  creator: 'Créateur',
  expert: 'Expert',
};

export function tierMeetsLyrraOneFeature(
  tier: LyrraOnePlanTier,
  feature: LyrraOnePlanFeature,
): boolean {
  // Sans date de compte : uniquement pendant la fenêtre d’inscription (fallback UI).
  if (summerAiPromoGrantsFeature(feature)) return true;
  return TIER_RANK[tier] >= TIER_RANK[LYRRAONE_FEATURE_MIN_TIER[feature]];
}

export function minTierLabelForFeature(feature: LyrraOnePlanFeature): string {
  const min = LYRRAONE_FEATURE_MIN_TIER[feature];
  return LYRRAONE_PLAN_LABELS[min];
}

export type LoPlanUsage = {
  plan: LyrraOnePlanTier;
  usage: Record<string, { used: number; limit: number }>;
  features: Record<LyrraOnePlanFeature, boolean>;
  maxSimpleModulesPerFormation: number;
};

export function hasLoPlanFeature(
  plan: LoPlanUsage | null | undefined,
  feature: LyrraOnePlanFeature,
): boolean {
  // Source de vérité : features renvoyées par l’API (inclut le crédit 6 mois).
  if (plan?.features) return plan.features[feature] === true;
  // Fallback pendant le chargement / hors session : fenêtre d’inscription seulement.
  if (summerAiPromoGrantsFeature(feature)) return true;
  return false;
}

/** Chemins sidebar marketing → fonctionnalité requise (absent = toujours visible). */
export const LYRRAONE_MARKETING_PATH_FEATURES: Partial<Record<string, LyrraOnePlanFeature>> = {
  '/marketing/site/blog': 'blog',
  '/marketing/affiliate': 'affiliateProgram',
  '/marketing/promotions': 'coupons',
  '/marketing/booking': 'booking',
  '/marketing/communities': 'communities',
  '/marketing/webinars': 'webinars',
  '/marketing/physical-products': 'physicalProducts',
  '/marketing/physical-orders': 'physicalProducts',
  '/marketing/sms': 'smsMarketing',
};

/** Clés modules hub marketing (`LYRRAONE_HUB_MODULES`). */
export const LYRRAONE_HUB_MODULE_FEATURES: Partial<
  Record<string, LyrraOnePlanFeature>
> = {
  blog: 'blog',
  affiliate: 'affiliateProgram',
  promotions: 'coupons',
  booking: 'booking',
  communities: 'communities',
  webinars: 'webinars',
  physical: 'physicalProducts',
  sms: 'smsMarketing',
};

export const LYRRAONE_INSTITUTION_TAB_FEATURES: Partial<
  Record<InstitutionTabId, LyrraOnePlanFeature>
> = {
  webinars: 'webinars',
  'bi-studio': 'detailedAnalytics',
};

export const LYRRAONE_EXTRA_SETTINGS_TAB_FEATURES: Partial<
  Record<LyrraOneExtraSettingsTabId, LyrraOnePlanFeature>
> = {
  sms: 'smsMarketing',
  'sms-templates': 'smsMarketing',
  'google-calendar': 'booking',
};

export function isMarketingPathAllowed(
  path: string,
  plan: LoPlanUsage | null | undefined,
): boolean {
  const feature = LYRRAONE_MARKETING_PATH_FEATURES[path];
  if (!feature) return true;
  return hasLoPlanFeature(plan, feature);
}

export function isHubModuleAllowed(
  moduleKey: string,
  plan: LoPlanUsage | null | undefined,
): boolean {
  const feature = LYRRAONE_HUB_MODULE_FEATURES[moduleKey];
  if (!feature) return true;
  return hasLoPlanFeature(plan, feature);
}

export function isInstitutionTabAllowed(
  tabId: InstitutionTabId,
  plan: LoPlanUsage | null | undefined,
): boolean {
  const feature = LYRRAONE_INSTITUTION_TAB_FEATURES[tabId];
  if (!feature) return true;
  return hasLoPlanFeature(plan, feature);
}

export const LYRRAONE_SUBSCRIPTION_PATH = '/profile?tab=subscription';
